import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { getAuthHeaders } from '../util';
import { useAuth, isStandalone } from '../platform';

// Translation context
const TranslationContext = createContext();

// Language ID mapping (matches backend ChosenLang enum in lang.go)
// LangAuto = 0, LangEnglish = 1, LangGerman = 2
const LANGUAGE_IDS = {
  'en': 1,  // LangEnglish
  'de': 2,  // LangGerman
};

// Default language
const DEFAULT_LANGUAGE = 'en';

// RTL languages (none currently supported)
const RTL_LANGUAGES = [];

export function TranslationProvider({ children }) {
  const { user, isLoading: authLoading } = useAuth();
  const [currentLang, setCurrentLang] = useState(DEFAULT_LANGUAGE);
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load translations from backend (which uses the chat's actual language setting)
  const loadTranslations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/translations', {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load translations: ${response.status}`);
      }
      
      const data = await response.json();

      // Handle both wrapped format (from webAppRespondJSON) and direct format
      const result = data.result || data;

      if (result && result.translations && result.current_language) {
        setTranslations(result.translations);
        setCurrentLang(result.current_language);

        // Update localStorage to sync with backend setting
        localStorage.setItem('app_language', result.current_language);
      } else {
        throw new Error('Invalid translation response format');
      }
      
    } catch (err) {
      console.error('Translation loading error:', err);
      setError(err.message);
      
      // Fallback to default language and basic translations on error
      setCurrentLang(DEFAULT_LANGUAGE);
      setTranslations({});
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize translations on mount (or when auth completes for standalone)
  useEffect(() => {
    // In standalone mode, wait for user to be set (including guest user) before loading translations
    if (isStandalone && (authLoading || !user)) {
      setLoading(false); // Don't show loading state while waiting for auth
      return;
    }

    // Load translations from backend (which uses the chat's actual language setting)
    loadTranslations();
  }, [user, authLoading, loadTranslations]);

  // Change language and persist choice
  const changeLanguage = async (langCode) => {
    if (langCode === currentLang) return;
    
    try {
      // Convert language code to language ID for backend
      const langId = LANGUAGE_IDS[langCode] || LANGUAGE_IDS[DEFAULT_LANGUAGE];
      
      // Update language setting on backend
      const response = await fetch(`/api/settings/lang/${langId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Failed to change language: ${response.status}`);
      }
      
      // Reload translations with new language setting
      await loadTranslations();
      
    } catch (err) {
      console.error('Language change error:', err);
      setError(err.message);
    }
  };

  // Pluralization logic for English and German (both use similar rules)
  const getPluralForm = (count, forms) => {
    if (forms.length === 3) {
      // 3 forms: {zero|singular|plural}
      if (count === 0) return forms[0];
      return count === 1 ? forms[1] : forms[2];
    } else {
      // 2 forms: {singular|plural}
      return count === 1 ? forms[0] : forms[1];
    }
  };

  // Translation function with parameter support
  const t = (key, ...params) => {
    let template = translations[key] || key;
    
    // Handle _num suffix for pluralization
    if (key.endsWith('_num') && params.length > 0) {
      const count = params[0];
      
      // Check if template contains pluralization syntax {option1|option2|option3}
      const pluralMatch = template.match(/\{([^}]+)\}/);
      if (pluralMatch) {
        const forms = pluralMatch[1].split('|');
        const selectedForm = getPluralForm(count, forms);
        
        // Replace the {forms} with selected form
        template = template.replace(/\{[^}]+\}/, selectedForm);
        
        // Replace %d within the selected form with the count
        template = template.replace(/%d/, String(count));
        
        // Continue with remaining parameters (skip the count as it's already used)
        params = params.slice(1);
      }
    }
    
    if (params.length === 0) {
      return template;
    }
    
    // Simple parameter replacement for remaining %s and %d
    let result = template;
    params.forEach(param => {
      result = result.replace(/%[sd]/, String(param));
    });
    
    return result;
  };

  // Check if a translation exists
  const hasTranslation = (key) => {
    return key in translations;
  };

  // Get the appropriate locale for date formatting
  const getLocale = () => {
    const localeMap = {
      'en': 'en-US',
      'de': 'de-DE',
    };
    return localeMap[currentLang] || 'en-US';
  };

  // RTL detection function
  const isRTL = () => {
    return RTL_LANGUAGES.includes(currentLang);
  };

  const value = {
    currentLang,
    locale: getLocale(),
    translations,
    loading,
    error,
    t,
    hasTranslation,
    changeLanguage,
    isRTL,
    availableLanguages: Object.keys(LANGUAGE_IDS),
    languageNames: {
      'en': 'English',
      'de': 'Deutsch',
    }
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

// Hook to use translations
export function useTranslation() {
  const context = useContext(TranslationContext);
  
  if (!context) {
    throw new Error('useTranslation must be used within TranslationProvider');
  }
  
  return context;
}

// Optional: Higher-order component for class components
export function withTranslation(Component) {
  return function TranslatedComponent(props) {
    const translation = useTranslation();
    return <Component {...props} {...translation} />;
  };
}